(() => {
  'use strict';
  const VERSION = '2026-10-02.9';
  if (window.LDQuantityUpsell?.version === VERSION) return;
  window.LDQuantityUpsell?.dispose?.();
  if (window.LDQuantityUpsell?.capture) {
    window.removeEventListener('click', window.LDQuantityUpsell.capture, true);
    window.removeEventListener('submit', window.LDQuantityUpsell.capture, true);
  }
  let flow = null;
  let stylesheet = null;
  let memoryDeclines = 0;
  const offerCache = new Map();
  const SESSION_KEY = 'ld_qty_offer_declines';
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  async function bounded(task, ms, message) {
    let timer;
    try { return await Promise.race([task, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(message)), ms); })]); }
    finally { clearTimeout(timer); }
  }
  const root = () => document.querySelector('[data-ld-quantity-upsell]');
  const route = (file) => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + file;
  const money = (cents) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(cents / 100);
  const stableProps = (value) => JSON.stringify(Object.entries(value || {}).filter(([, v]) => v != null && v !== '').sort(([a], [b]) => a.localeCompare(b)));
  const variantId = (item) => String(item?.variant_id ?? item?.id ?? '');
  function quotePercentage(unitCents, qty, percent, currentCents = unitCents) {
    const bps = Math.round(Number(percent) * 100);
    if (![unitCents, qty, currentCents, bps].every(Number.isSafeInteger) ||
        unitCents <= 0 || unitCents > 100000000 || ![2, 3].includes(qty) ||
        bps <= 0 || bps >= 10000 || Math.abs(Number(percent) * 100 - bps) > 0.00001) return null;
    const original = unitCents * qty;
    const total = original - Math.floor(original * bps / 10000);
    return { qty, percent: Number(percent), original, total, extra: total - currentCents };
  }
  function config() {
    try {
      const data = JSON.parse(root().querySelector('[data-ld-upsell-config]').textContent);
      if (!data.enabled || (!data.allowLive && String(window.Shopify?.theme?.role || '').toLowerCase() === 'main')) return null;
      return { ...data, sizes: data.sizes.map((s) => String(s).trim().toUpperCase()) };
    } catch (_) { return null; }
  }
  function quoteOffer(unitCents, offer, currentCents = unitCents) {
    if (offer.method === 'percentage') return quotePercentage(unitCents, offer.qty, offer.amount, currentCents);
    if (![unitCents, currentCents, offer.qty, offer.amount].every(Number.isSafeInteger) ||
        unitCents <= 0 || unitCents > 100000000 || ![2, 3].includes(offer.qty) || offer.amount <= 0) return null;
    const original = unitCents * offer.qty;
    const total = offer.method === 'exact_price' ? Math.min(offer.amount, original) :
      offer.method === 'fixed_amount' ? Math.max(0, unitCents - offer.amount) * offer.qty : null;
    return total == null ? null : { qty: offer.qty, original, total, extra: total - currentCents };
  }
  function productFor(form) {
    try { return JSON.parse(form.querySelector('[data-ld-qty-product]').textContent); } catch (_) { return null; }
  }
  function sizeFor(product, variant) {
    const dimensions = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    return String(product.size || dimensions || /^[LŁ]D-([A-Z]+)-/i.exec(variant?.sku || '')?.[1] || '').toUpperCase();
  }
  function eligibleVariant(product, variant, cfg) {
    return variant?.available && !variant.requiresSellingPlan && /^[LŁ]D-[A-Z]+-\d+$/i.test(variant.sku || '') &&
      (cfg.allSizes || cfg.sizes.includes(sizeFor(product, variant)));
  }
  function declines() {
    try { return Math.max(memoryDeclines, Number(sessionStorage.getItem(SESSION_KEY)) || 0); } catch (_) { return memoryDeclines; }
  }
  function recordDecline() {
    memoryDeclines = Math.min(2, declines() + 1);
    try { sessionStorage.setItem(SESSION_KEY, String(memoryDeclines)); } catch (_) {}
  }
  function skipped(form, product) {
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    const type = String(product.offerType || '').toLowerCase();
    if (product.giftCard || /^tatuaz-na-zamowienie/i.test(product.handle) ||
        tags.some((t) => /dziara-na-zamowienie|gratis|free-gift|bundle|stylizacja|zestaw|noforupsell|opus-addons/.test(t)) ||
        /dziara-na-zamowienie|zestaw|rekawic|karta-podarunkowa/.test(product.template || '') ||
        /zestaw|stylizac|gratis|akcesor|dodatek/.test(type) ||
        /rekawic|ochrona-przesylki|ochrona-przesy/.test(product.handle) ||
        (product.type != null && !/^tatuaż półtrwały/i.test(product.type)) ||
        form.querySelector('[name="selling_plan"]')?.value ||
        form.closest('[class*="easify"],[id*="easify"]') ||
        form.querySelector('[class*="easify"],[id*="easify"],input[type="file"]')) return true;
    if (Array.isArray(window.LDC_SKIP)) {
      for (const skip of window.LDC_SKIP) {
        if (typeof skip !== 'string') continue;
        if (product.handle === skip || product.handle.startsWith(skip.replace(/\*$/, ''))) return true;
        try { if (form.matches(skip) || form.closest(skip) || form.querySelector(skip)) return true; } catch (_) {}
      }
    }
    return false;
  }
  function matchingLine(cart, id, props) {
    const lines = cart.items.filter((i) => variantId(i) === String(id) &&
      stableProps(i.properties) === stableProps(props) && !i.selling_plan_allocation &&
      !i.parent_relationship && !i.item_components?.length);
    return lines.length === 1 ? lines[0] : null;
  }
  function countVariant(cart, id) {
    return cart.items.filter((i) => variantId(i) === String(id)).reduce((sum, i) => sum + i.quantity, 0);
  }
  async function request(url, body, asText = false) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const isForm = body instanceof FormData;
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: asText ? 'text/html' : 'application/json', ...(body && !isForm ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: isForm ? body : JSON.stringify(body) } : {}), signal: controller.signal
      });
      const data = asText ? await response.text() : await response.json();
      if (!response.ok || (!asText && data?.status >= 400)) throw new Error(data?.description || data?.message || 'Błąd sieci lub koszyka.');
      return data;
    } finally { clearTimeout(timer); }
  }
  async function getCart() {
    const cart = await request(route('cart.js'));
    if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) throw new Error('Niepełna odpowiedź koszyka.');
    return cart;
  }
  function lock(f, locked) {
    f.button.disabled = locked;
    f.button.setAttribute('aria-busy', String(locked));
    f.button.classList.toggle('loading', locked);
    f.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !locked);
    if (!locked) {
      f.button.disabled = f.buttonWasDisabled;
      f.button.removeAttribute('aria-busy');
    }
  }
  function suppressCart(f) {
    const paths = [...new Set([location.pathname, location.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '')])];
    const existing = Array.isArray(window.essentialCartPreventAutoOpenPaths) ? window.essentialCartPreventAutoOpenPaths : [];
    const owned = paths.filter((path) => !existing.includes(path));
    window.essentialCartPreventAutoOpenPaths = [...existing, ...owned];
    let unsubscribe = null;
    let released = false;
    const bind = () => {
      if (!unsubscribe && typeof window.essentialCartApi?.onBeforeOpen === 'function') unsubscribe = window.essentialCartApi.onBeforeOpen(() => false);
    };
    bind();
    const timer = setInterval(bind, 100);
    return () => {
      if (released) return;
      released = true;
      clearInterval(timer);
      unsubscribe?.();
      if (Array.isArray(window.essentialCartPreventAutoOpenPaths)) window.essentialCartPreventAutoOpenPaths = window.essentialCartPreventAutoOpenPaths.filter((p) => !owned.includes(p));
    };
  }
  function track(f, name, detail = {}) {
    const payload = { product_id: f.product.id, variant_id: Number(f.variant), size: f.size,
      unit_price: (f.line?.final_line_price ?? f.variantData.price) / 100, currency: 'PLN', ...detail };
    try {
      const published = window.Shopify?.analytics?.publish?.(name, payload);
      if (published?.catch) published.catch((e) => console.error('[ŁD quantity offer] Customer Events', e));
    } catch (e) { console.error('[ŁD quantity offer] Customer Events', e); }
  }
  function filterMatches(discount, product, cfg) {
    if (discount.status !== 'published' || discount.exclude_b2b && cfg.b2b ||
        (discount.excluded_customer_tags || []).some((t) => (cfg.customerTags || []).includes(t))) return false;
    if (Object.keys(discount).some((k) => /market|country/i.test(k) && discount[k] != null && discount[k] !== false)) return false;
    const ids = (discount.filter_rule_resources || []).map((id) => String(id).split('/').pop());
    if (discount.filter_rule === 'any_of_selected_collections') return product.collections.some((id) => ids.includes(String(id)));
    if (discount.filter_rule === 'any_of_selected_products') return ids.includes(String(product.id));
    if (discount.filter_rule === 'all_products') return true;
    return false;
  }
  async function fetchOffer(f) {
    const url = new URL(route('products/' + encodeURIComponent(f.product.handle)), location.origin);
    if (String(window.Shopify?.theme?.role || '').toLowerCase() !== 'main' && window.Shopify?.theme?.id) url.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
    const html = await request(url.href, null, true);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const freshNodes = [...doc.querySelectorAll('[data-ld-qty-product]')];
    const fresh = freshNodes.map((n) => { try { return JSON.parse(n.textContent); } catch (_) { return null; } }).find((p) => p && String(p.id) === String(f.product.id));
    if (!fresh) return null;
    const variant = fresh.variants.find((v) => String(v.id) === f.variant);
    if (!eligibleVariant(fresh, variant, f.cfg) || skipped(f.form, fresh)) return null;
    const discounts = new Map();
    for (const node of doc.querySelectorAll('.bx-widget-settings[data-bx-backend-data]')) {
      try {
        const data = JSON.parse(node.getAttribute('data-bx-backend-data'));
        for (const d of data.discounts || []) if (filterMatches(d, fresh, f.cfg)) discounts.set(d.id, d);
      } catch (_) {}
    }
    if (discounts.size !== 1) return null;
    const discount = [...discounts.values()][0];
    if (discount.use_compare_at_price || discount.variants?.enabled) return null;
    const offers = [];
    for (const qty of [2, 3]) {
      const matches = (discount.offers || []).filter((o) => Number(o.quantity_threshold) === qty);
      if (matches.length !== 1) continue;
      const offer = matches[0];
      if (!['percentage', 'exact_price', 'fixed_amount'].includes(offer.discount_method) ||
          offer.extras?.length || offer.gift_selectors?.length || offer.variant_ids?.length) continue;
      const max = variant.quantityRule?.max;
      const min = variant.quantityRule?.min || 1;
      const increment = variant.quantityRule?.increment || 1;
      if (qty < min || max != null && qty > max || (qty - min) % increment !== 0) continue;
      if (variant.inventoryManagement && variant.inventoryPolicy === 'deny' && (!Number.isFinite(variant.inventoryQuantity) || variant.inventoryQuantity < qty)) continue;
      offers.push({ qty, method: offer.discount_method, amount: Number(offer.discount_amount), discountId: discount.id, offerId: offer.oid });
    }
    return { product: fresh, variant, offers };
  }
  function loadOffer(f) {
    const key = f.variant + ':' + String(window.Shopify?.theme?.id || '');
    const cached = offerCache.get(key);
    if (cached && Date.now() - cached.time < 30000) return cached.promise;
    const entry = { time: Date.now(), promise: null };
    entry.promise = fetchOffer(f).catch((error) => { offerCache.delete(key); throw error; });
    offerCache.set(key, entry);
    return entry.promise;
  }
  async function loadStyles() {
    if (!stylesheet) stylesheet = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = root().dataset.stylesheet;
      link.onload = () => resolve(); link.onerror = () => reject(new Error('Nie udało się wczytać panelu.'));
      document.head.append(link);
    });
    await bounded(stylesheet, 10000, 'Panel nie został wczytany.');
  }
  function busy(f, value) {
    f.busy = value;
    f.dialog?.setAttribute('aria-busy', String(value));
    f.dialog?.querySelectorAll('button').forEach((b) => { b.disabled = value; });
    const progress = f.dialog?.querySelector('[data-ld-upsell-progress]');
    if (progress) progress.hidden = !value;
  }
  function show(f) {
    const host = root();
    if (!host.querySelector('dialog')) host.append(host.querySelector('template').content.cloneNode(true));
    const dialog = host.querySelector('dialog');
    f.dialog = dialog;
    // Essential's native-drawer cleanup calls close() on every open dialog,
    // even when its automatic cart opening is disabled. That is not a decline.
    dialog.close = function (...args) {
      if (flow?.dialog === this && !flow.finishing) {
        queueMicrotask(() => { if (flow?.dialog === this && !flow.finishing) document.body.style.overflow = 'hidden'; });
        return;
      }
      HTMLDialogElement.prototype.close.apply(this, args);
    };
    dialog.classList.remove('ld-qu--closing');
    dialog.setAttribute('aria-describedby', 'LDQuantityUpsellDescription');
    dialog.querySelector('[data-ld-upsell-title]').textContent = f.product.title + ' (' + f.size + ') w koszyku';
    const img = dialog.querySelector('[data-ld-upsell-image]');
    if (f.product.image) { img.src = f.product.image; img.hidden = false; } else img.hidden = true;
    for (const button of dialog.querySelectorAll('[data-ld-upsell-qty]')) {
      const quote = f.quotes.find((q) => q.qty === Number(button.dataset.ldUpsellQty));
      button.hidden = !quote;
      if (!quote) continue;
      button.querySelector('[data-ld-upsell-action]').classList.remove('is-added');
      button.querySelector('[data-ld-upsell-add-label]').textContent = 'Dorzuć ' + (quote.qty - 1) + ' szt. za ' + money(quote.extra);
      button.querySelector('[data-ld-upsell-compare]').textContent = money(quote.original);
      button.querySelector('[data-ld-upsell-total]').textContent = money(quote.total);
      button.querySelector('[data-ld-upsell-unit-price]').textContent = money(quote.total / quote.qty);
      button.setAttribute('aria-label', 'Dorzuć ' + (quote.qty - 1) + ' szt. za ' + money(quote.extra) + '. ' + quote.qty + ' sztuki łącznie, cena łączna ' + money(quote.total) + ', średnio ' + money(quote.total / quote.qty) + ' za sztukę');
    }
    syncButtonStyle();
    busy(f, false);
    f.overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.onclick = (event) => {
      if (f !== flow || f.busy) return;
      const offer = event.target.closest('[data-ld-upsell-qty]');
      if (offer) { void select(f, Number(offer.dataset.ldUpsellQty)); return; }
      if (event.target.closest('[data-ld-upsell-close]')) { void decline(f, 'button'); return; }
      if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) void decline(f, 'backdrop');
      }
    };
    dialog.oncancel = (event) => { event.preventDefault(); if (!f.busy) void decline(f, 'esc'); };
    dialog.onclose = () => {
      if (f === flow && !f.finishing && !dialog.open) {
        dialog.showModal();
        document.body.style.overflow = 'hidden';
        dialog.querySelector('[data-ld-upsell-close]').focus({ preventScroll: true });
      }
    };
    let start = null;
    dialog.onpointerdown = (event) => {
      if (!f.busy && !event.target.closest('button') && event.isPrimary && (event.target.closest('.ld-qu__head') || event.target.closest('[data-ld-upsell-handle]'))) {
        start = { x: event.clientX, y: event.clientY };
        dialog.setPointerCapture?.(event.pointerId);
      }
    };
    dialog.onpointerup = (event) => {
      if (start && event.clientY - start.y > 65 && Math.abs(event.clientX - start.x) < 70 && !f.busy) void decline(f, 'swipe');
      start = null;
    };
    dialog.onpointercancel = () => { start = null; };
    dialog.showModal();
    dialog.querySelector('[data-ld-upsell-close]').focus({ preventScroll: true });
    track(f, 'ld_qty_offer_shown');
  }
  async function refreshAndOpen(f) {
    if (f.finishing) return;
    f.finishing = true;
    f.phase = 'cart';
    busy(f, true);
    try {
      if (f.dialog?.open) {
        f.dialog.classList.add('ld-qu--closing');
        await wait(matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
        f.dialog.close();
      }
      if (f.overflow !== undefined) document.body.style.overflow = f.overflow;
      const nativeDrawer = document.querySelector('cart-drawer');
      try {
        // Essential owns its drawer and counter. Rendering Dawn as well races
        // its open/focus handlers and adds an unnecessary section request.
        if (typeof window.essentialCartApi?.openCart === 'function') {
          await openEssential(f);
          return;
        }
        const cart = f.cart || await getCart();
        const sectionsUrl = new URL(location.href);
        sectionsUrl.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
        const sections = await request(sectionsUrl.href);
        if (nativeDrawer?.renderContents && sections['cart-drawer'] && sections['cart-icon-bubble']) {
          nativeDrawer.classList.toggle('is-empty', cart.item_count === 0);
          nativeDrawer.suppressNextOpen = true;
          nativeDrawer.setActiveElement?.(f.button);
          nativeDrawer.renderContents({ id: Number(f.variant), sections });
          await wait(30);
        } else throw new Error('Brak aktualnych sekcji koszyka.');
        f.release();
        if (nativeDrawer?.open) nativeDrawer.open(f.button);
        else location.assign(route('cart'));
      } catch (error) {
        console.error('[ŁD quantity offer] Cart refresh', error);
        f.release();
        location.assign(route('cart'));
      }
    } finally {
      f.release();
      lock(f, false);
      if (flow === f) flow = null;
    }
  }
  function cartSignature(cart) {
    return JSON.stringify((cart?.items || []).map((item) => [variantId(item), item.quantity, item.final_line_price, stableProps(item.properties)]).sort());
  }
  function cartIncludes(actual, expected) {
    const remaining = (actual?.items || []).map((item) => cartSignature({ items: [item] }));
    return expected.items.every((item) => {
      const index = remaining.indexOf(cartSignature({ items: [item] }));
      if (index < 0) return false;
      remaining.splice(index, 1);
      return true;
    });
  }
  async function openEssential(f) {
    let active = true;
    try { await bounded((async () => {
      // The public API can exist before its internal controller is mounted.
      // Its methods then return successfully without doing anything.
      while (active && !Array.isArray(window.essentialCartApi?.getCartData?.()?.items)) await wait(50);
      if (!active) return;
      const api = window.essentialCartApi;
      if (!f.cart || cartSignature(api.getCartData()) !== cartSignature(f.cart)) {
        await api.refreshCart();
      }
      // refreshCart resolves before React commits the new controller/data.
      while (active && f.cart && !cartIncludes(window.essentialCartApi?.getCartData?.(), f.cart)) await wait(50);
    })(), 3500, 'Essential nie udostępnił aktualnego koszyka.'); }
    finally { active = false; }
    f.release();
    for (let attempt = 0; attempt < 3; attempt++) {
      window.essentialCartApi.openCart();
      for (let tick = 0; tick < 5; tick++) {
        await wait(50);
        if (window.essentialCartApi?.isOpen?.() || document.documentElement.classList.contains('essential-cart__open')) return;
      }
    }
    throw new Error('Essential nie otworzył szuflady.');
  }
  async function openCartAfterAdd() {
    if (flow) return;
    const cart = await getCart();
    await openEssential({ cart, release: () => {} });
  }
  async function decline(f, method) {
    if (f !== flow || f.busy || f.finishing) return;
    busy(f, true);
    recordDecline();
    track(f, 'ld_qty_offer_declined', { method });
    await refreshAndOpen(f);
  }
  async function select(f, qty) {
    if (f !== flow || f.busy || f.finishing) return;
    const quote = f.quotes.find((q) => q.qty === qty);
    if (!quote) return;
    busy(f, true);
    track(f, 'ld_qty_offer_selected', { qty, discount_method: quote.method, discount_amount: quote.amount, bundlex_offer_id: quote.offerId });
    try {
      const before = await getCart();
      const line = matchingLine(before, f.variant, f.props);
      if (!line || line.quantity !== 1 || countVariant(before, f.variant) !== 1 ||
          line.final_line_price !== f.line.final_line_price || line.original_price !== f.line.original_price) return;
      let addError = null;
      try { await request(route('cart/add.js'), { items: [{ id: Number(f.variant), quantity: qty - 1, properties: f.props }] }); }
      catch (error) { addError = error; console.error('[ŁD quantity offer] Additional add', error); }
      const cart = await getCart();
      f.cart = cart;
      const actual = matchingLine(cart, f.variant, f.props);
      if (!actual || actual.quantity !== qty || actual.final_line_price !== quote.total) {
        console.error('[ŁD quantity offer] Price mismatch', { variant_id: f.variant, qty, expected: quote.total, actual: actual?.final_line_price, actual_quantity: actual?.quantity });
        track(f, 'ld_qty_offer_price_mismatch', { qty, expected_total: quote.total / 100, actual_total: actual ? actual.final_line_price / 100 : null, actual_qty: actual?.quantity ?? null });
      } else if (!addError) {
        const selected = f.dialog.querySelector('[data-ld-upsell-qty="' + qty + '"]');
        selected.querySelector('[data-ld-upsell-action]').classList.add('is-added');
        selected.querySelector('[data-ld-upsell-add-label]').textContent = 'Dodano ✓';
        selected.setAttribute('aria-label', 'Dodano. ' + qty + ' sztuki w koszyku.');
        await wait(500);
      }
    } catch (error) { console.error('[ŁD quantity offer] Verification', error); }
    finally { await refreshAndOpen(f); }
  }
  async function start(f) {
    flow = f;
    f.release = suppressCart(f);
    lock(f, true);
    f.phase = 'adding';
    try {
      const before = await getCart();
      const eligible = declines() < 2 && countVariant(before, f.variant) === 0;
      const quoteData = eligible ? loadOffer(f).catch((e) => { console.error('[ŁD quantity offer] Bundlex source', e); return null; }) : Promise.resolve(null);
      let addError = null;
      try { await request(route('cart/add.js'), f.formData); } catch (error) { addError = error; }
      const cart = await getCart();
      f.cart = cart;
      f.line = matchingLine(cart, f.variant, f.props);
      if (addError && (!f.line || countVariant(cart, f.variant) !== countVariant(before, f.variant) + 1)) {
        const host = f.form.closest('product-form');
        const wrapper = host?.querySelector('.product-form__error-message-wrapper');
        const message = host?.querySelector('.product-form__error-message');
        if (wrapper && message) { message.textContent = addError.message; wrapper.hidden = false; }
      }
      const data = await quoteData;
      if (!eligible || !data || cart.currency !== 'PLN' || !f.line || countVariant(cart, f.variant) !== 1 || f.line.quantity !== 1 ||
          !Number.isSafeInteger(f.line.final_line_price) || data.variant.price !== f.line.original_price) {
        await refreshAndOpen(f); return;
      }
      f.product = data.product;
      f.variantData = data.variant;
      f.size = sizeFor(data.product, data.variant);
      f.quotes = data.offers.map((o) => ({ ...quoteOffer(data.variant.price, o, f.line.final_line_price), ...o }))
        .filter((o) => Number.isSafeInteger(o.total) && o.extra > 0 && o.total < o.original);
      if (!f.quotes.length || typeof HTMLDialogElement === 'undefined') { await refreshAndOpen(f); return; }
      await loadStyles();
      f.phase = 'offer';
      show(f);
    } catch (error) {
      console.error('[ŁD quantity offer] Add flow', error);
      await refreshAndOpen(f);
    }
  }
  function syncButtonStyle() {
    const f = flow;
    if (!f?.dialog || f.finishing) return;
    const source = f.button.classList.contains('ld-card-atc') ? f.button : document.querySelector('.ld-card-form .ld-card-atc');
    if (!source) return;
    const height = source.getBoundingClientRect().height;
    if (height <= 0) return;
    const style = getComputedStyle(source);
    const values = { height: height + 'px', size: style.fontSize, line: style.lineHeight, radius: style.borderRadius, padding: style.padding, spacing: style.letterSpacing };
    for (const [key, value] of Object.entries(values)) f.dialog.style.setProperty('--ld-qu-action-' + key, value);
  }
  function capture(event) {
    const button = event.type === 'click' ? event.target.closest?.('button[type="submit"],input[type="submit"]') : event.submitter || event.target.querySelector?.('[type="submit"]');
    const form = event.type === 'submit' ? event.target : button?.form;
    if (!form?.closest('product-form') || !button || event.defaultPrevented || button.disabled) return;
    const product = productFor(form);
    if (!product) return;
    const cfg = config();
    if (!cfg || !form.classList.contains('ld-card-form') && !cfg.enablePdp || skipped(form, product)) return;
    if (flow) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    const formData = new FormData(form);
    const id = String(formData.get('id') || '');
    const variant = product.variants.find((v) => String(v.id) === id);
    const size = sizeFor(product, variant);
    if (!eligibleVariant(product, variant, cfg) || Number(formData.get('quantity') || 1) !== 1) return;
    const props = {};
    for (const [key, value] of formData) {
      if (value instanceof File && value.size) return;
      const name = /^properties\[(.+)\]$/.exec(key)?.[1];
      if (name) props[name] = String(value);
    }
    formData.set('quantity', '1');
    formData.delete('sections');
    formData.delete('sections_url');
    event.preventDefault();
    event.stopImmediatePropagation();
    void start({ form, button, product, cfg, variant: id, variantData: variant, size, props, formData, buttonWasDisabled: button.disabled, busy: true });
  }
  function warm(event) {
    if (flow || declines() >= 2 || offerCache.size >= 4) return;
    const button = event.target.closest?.('button[type="submit"]');
    const form = button?.form;
    if (!form?.closest('product-form')) return;
    const product = productFor(form), cfg = config();
    if (!product || !cfg || skipped(form, product) || !form.classList.contains('ld-card-form') && !cfg.enablePdp) return;
    const variant = String(form.querySelector('[name="id"]')?.value || '');
    if (!eligibleVariant(product, product.variants.find((v) => String(v.id) === variant), cfg)) return;
    void loadOffer({ product, cfg, form, variant }).catch(() => {});
    void loadStyles().catch(() => {});
  }
  function escape(event) {
    const f = flow;
    if (event.key !== 'Escape' || !f?.dialog?.open) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!f.busy && !f.finishing) void decline(f, 'esc');
  }
  function dispose() {
    window.removeEventListener('click', capture, true);
    window.removeEventListener('submit', capture, true);
    window.removeEventListener('keydown', escape, true);
    window.removeEventListener('pointerover', warm, true);
    window.removeEventListener('focusin', warm, true);
    window.removeEventListener('pointerdown', warm, true);
    window.removeEventListener('resize', syncButtonStyle);
    if (flow) {
      flow.finishing = true;
      flow.release();
      lock(flow, false);
      flow.dialog?.close();
      if (flow.overflow !== undefined) document.body.style.overflow = flow.overflow;
    }
    flow = null;
  }
  window.LDQuantityUpsell = { version: VERSION, capture, quotePercentage, quoteOffer, openCartAfterAdd, dispose,
    status: () => flow ? { phase: flow.phase, busy: flow.busy, variant_id: flow.variant } : { phase: 'idle' } };
  window.addEventListener('click', capture, true);
  window.addEventListener('submit', capture, true);
  window.addEventListener('keydown', escape, true);
  window.addEventListener('pointerover', warm, true);
  window.addEventListener('focusin', warm, true);
  window.addEventListener('pointerdown', warm, true);
  window.addEventListener('resize', syncButtonStyle);
})();
